import { Box, Button, Stack, Tab, Tabs, styled } from "@mui/material";
import { SyntheticEvent } from "react";

import { Certificate } from "./Certificate/Certificate";
import { ProcedureAndExams } from "./ProcedureAndExams/ProcedureAndExams";

// Ícones para cada tab de intervenções
import { Add, Search } from "@mui/icons-material";
import MedicationIcon from "@mui/icons-material/Medication"; // Tratamentos
import BiotechIcon from "@mui/icons-material/Biotech"; // Exames
import MedicalServicesIcon from "@mui/icons-material/MedicalServices"; // Procedimentos
import DescriptionIcon from "@mui/icons-material/Description"; // Relatórios
import AssignmentIcon from "@mui/icons-material/Assignment"; // Questionários
import { useInterventionsContext } from "../../../contexts/InterventionsContext";
import { useMedicalCareContext } from "../../../contexts/MedicalCareContext";
import { CreateProtocolDialog } from "./CreateProtocolDialog";
import { SelectProtocolDialog } from "./SelectProtocolDialog";
import { KeyPressShortCutInterventionTabs } from "./KeyPressShortcuts/KeyPressShortcut";
import { ListaDeIntervenções } from "./ListaDeIntervenções";
import { PrescriptionCare } from "./PrescriptionCare/PrescriptionCare";
import LuxorMed from "@/components/luxor/LuxorMed";
import QuestionnairesTabPanel from "./QuestionnairesTabPanel";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  selectedInterventions: any;
  functions: any;
  setCreateDialog: any;
  expandedList: any;
  setExpandedList: any;
  appliedProtocol: any;
}

function TabPanel(props: TabPanelProps) {
  const {
    children,
    value,
    index,
    selectedInterventions,
    functions,
    setCreateDialog,
    expandedList,
    setExpandedList,
    appliedProtocol,
    ...other
  } = props;

  const { luxorMode } = useMedicalCareContext();

  return (
    <div
      role="tabpanel"
      style={{ width: "100%" }}
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Stack
          sx={{ pl: 3, width: "100%" }}
          direction="row"
          justifyContent="space-between"
        >
          <Stack
            width="100%"
            direction="row"
            justifyContent="space-between"
            spacing={2}
            pr={3}
          >
            {children}

            <Stack
              width="100%"
              minWidth="600px"
              overflow="auto"
              maxHeight="60vh"
              paddingRight={3}
            >
              {!luxorMode && (
                <ListaDeIntervenções
                  selectedInterventions={selectedInterventions}
                  functions={functions}
                  setCreateDialog={setCreateDialog}
                  expandedList={expandedList}
                  setExpandedList={setExpandedList}
                  appliedProtocol={appliedProtocol}
                />
              )}
              {luxorMode && <LuxorMed />}
            </Stack>
          </Stack>
        </Stack>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

// Tabs com scroll vertical e indicador à esquerda (borda verde)
const ScrollableTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    left: 0, // Indicador à esquerda
    right: "auto",
    width: "4px", // Largura da borda verde
    backgroundColor: "#00897b", // Cor verde teal
  },
});

// Tab estilizada: alinhada à esquerda, texto em maiúsculas, com ícone
const StyledTab = styled(Tab)({
  textAlign: "left",
  alignItems: "flex-start", // Alinha conteúdo à esquerda
  justifyContent: "flex-start",
  textTransform: "uppercase", // Texto em maiúsculas
  fontSize: "0.75rem", // Tamanho menor para caber o texto
  fontWeight: 600, // Peso da fonte aumentado para melhor legibilidade
  minHeight: "48px",
  paddingLeft: "16px",
  paddingRight: "30px",
  // Estilo para o wrapper do ícone e label
  "& .MuiTab-iconWrapper": {
    marginRight: "12px", // Espaço entre ícone e texto
    marginBottom: "0px", // Remove margem inferior padrão
  },
  // Aumenta o tamanho do ícone dentro da tab
  "& .MuiSvgIcon-root": {
    fontSize: "1.25rem", // Ícone um pouco maior
  },
  "&.Mui-selected": {
    backgroundColor: "rgba(0, 137, 123, 0.08)", // Fundo leve verde quando selecionado
    color: "#00897b", // Texto verde quando selecionado
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)", // Hover sutil
  },
});

export function TabsIntervenções() {
  const {
    selectedInterventions,
    createDialog,
    setCreateDialog,
    selectProtocol,
    setSelectProtocol,
    functions,
    clean,
    setClean,
    setSelectedItem,
    control,
    action,
    handleSubmit,
    isEdit,
    setValue,
    expandedList,
    setExpandedList,
    appliedProtocol,
    setAppliedProtocol,
  } = useInterventionsContext();

  const { interventionTab, setInterventionTab } = useMedicalCareContext();

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setSelectedItem(undefined);
    setClean(true);
    setInterventionTab(newValue);
  };

  return (
    <>
      <KeyPressShortCutInterventionTabs />
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "background.paper",
          display: "flex",
          height: "fit-content",
        }}
      >
        <ScrollableTabs
          orientation="vertical"
          variant="scrollable"
          value={interventionTab}
          onChange={handleChange}
          aria-label="Abas de intervenções médicas"
          sx={{ borderRight: 1, borderColor: "divider" }}
        >
          <StyledTab
            label="Tratamentos"
            icon={<MedicationIcon />}
            iconPosition="start"
            {...a11yProps(0)}
          />
          <StyledTab
            label="Exames"
            icon={<BiotechIcon />}
            iconPosition="start"
            {...a11yProps(1)}
          />
          <StyledTab
            label="Procedimentos"
            icon={<MedicalServicesIcon />}
            iconPosition="start"
            {...a11yProps(2)}
          />
          <StyledTab
            label="Relatórios"
            icon={<DescriptionIcon />}
            iconPosition="start"
            {...a11yProps(3)}
          />
          <StyledTab
            label="Questionários"
            icon={<AssignmentIcon />}
            iconPosition="start"
            {...a11yProps(4)}
          />
        </ScrollableTabs>
        <TabPanel
          value={interventionTab}
          index={0}
          selectedInterventions={selectedInterventions}
          functions={functions}
          setCreateDialog={setCreateDialog}
          setExpandedList={setExpandedList}
          expandedList={expandedList}
          appliedProtocol={appliedProtocol}
        >
          <PrescriptionCare clean={clean} setSelect={setSelectProtocol} />
        </TabPanel>
        <TabPanel
          value={interventionTab}
          index={1}
          selectedInterventions={selectedInterventions}
          functions={functions}
          setCreateDialog={setCreateDialog}
          setExpandedList={setExpandedList}
          expandedList={expandedList}
          appliedProtocol={appliedProtocol}
        >
          <ProcedureAndExams setSelect={setSelectProtocol} type="exams" />
        </TabPanel>
        <TabPanel
          value={interventionTab}
          index={2}
          selectedInterventions={selectedInterventions}
          functions={functions}
          setCreateDialog={setCreateDialog}
          setExpandedList={setExpandedList}
          expandedList={expandedList}
          appliedProtocol={appliedProtocol}
        >
          <ProcedureAndExams setSelect={setSelectProtocol} type="procedure" />
        </TabPanel>
        <TabPanel
          value={interventionTab}
          index={3}
          selectedInterventions={selectedInterventions}
          functions={functions}
          setCreateDialog={setCreateDialog}
          setExpandedList={setExpandedList}
          expandedList={expandedList}
          appliedProtocol={appliedProtocol}
        >
          <Stack width="100%" maxHeight="60vh" overflow="auto">
            <Button
              sx={{ width: "fit-content", textTransform: "none" }}
              startIcon={<Search />}
              onClick={() => setSelectProtocol(true)}
            >
              Selecionar protocolo salvo
            </Button>
            <Certificate
              control={control}
              setValue={setValue}
              action={action}
              setClean={setClean}
              handleSubmit={handleSubmit}
              isEdit={isEdit}
            />
          </Stack>
        </TabPanel>

        <TabPanel
          value={interventionTab}
          index={4}
          selectedInterventions={selectedInterventions}
          functions={functions}
          setCreateDialog={setCreateDialog}
          setExpandedList={setExpandedList}
          expandedList={expandedList}
          appliedProtocol={appliedProtocol}
        >
          <QuestionnairesTabPanel />
        </TabPanel>

        {selectProtocol && (
          <SelectProtocolDialog
            setAppliedProtocol={setAppliedProtocol}
            isOpen={selectProtocol}
            handleClose={() => setSelectProtocol(false)}
            addItem={functions?.handleAdd}
          />
        )}
        {createDialog && (
          <CreateProtocolDialog
            itens={selectedInterventions?.all}
            isOpen={createDialog}
            handleClose={() => setCreateDialog(false)}
          />
        )}
      </Box>
    </>
  );
}
